import { useEffect, useState, useMemo } from 'react';
import { Search, Users, Building2, Flame, AlertTriangle, MessageSquare } from 'lucide-react';
import api from '../services/api';

interface WarmthDistribution {
  cold: number;
  cool: number;
  warm: number;
  hot: number;
  very_hot: number;
}

interface CompanyConnection {
  company: string;
  count: number;
}

interface DormantContact {
  id: string;
  name: string;
  title: string | null;
  company: string | null;
  warmth: number;
  last_interaction: string | null;
}

interface WarmContact {
  id: string;
  name: string;
  title: string | null;
  company: string | null;
  warmth: number;
  factors: Record<string, number>;
}

interface SourceBreakdown {
  source: string;
  count: number;
}

interface MessageResult {
  id: number;
  conversation_id: string;
  direction: string;
  content: string;
  date: string | null;
  from_name: string;
  to_name: string;
  contact_name: string | null;
}

interface InsightsData {
  by_source: SourceBreakdown[];
  top_companies: CompanyConnection[];
  warmth_distribution: WarmthDistribution;
  dormant_contacts: DormantContact[];
  top_warm_contacts: WarmContact[];
  import_history: any[];
}

function WarmthBar({ score }: { score: number }) {
  const color = score > 60 ? '#ef4444' : score > 40 ? '#f59e0b' : score > 20 ? '#3b82f6' : '#6b7280';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--bg-primary)' }}>
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${score}%`, background: color }}
        />
      </div>
      <span className="text-xs font-mono w-8 text-right" style={{ color }}>{score}</span>
    </div>
  );
}

function WarmthHeatmap({ distribution }: { distribution: WarmthDistribution }) {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0) || 1;
  const segments = [
    { label: 'Cold (0)', count: distribution.cold, color: '#6b7280' },
    { label: 'Cool (1-20)', count: distribution.cool, color: '#3b82f6' },
    { label: 'Warm (21-40)', count: distribution.warm, color: '#22c55e' },
    { label: 'Hot (41-60)', count: distribution.hot, color: '#f59e0b' },
    { label: 'Very Hot (61+)', count: distribution.very_hot, color: '#ef4444' },
  ];

  return (
    <div>
      {/* Bar */}
      <div className="flex h-6 rounded-lg overflow-hidden mb-3">
        {segments.map(seg => (
          seg.count > 0 && (
            <div
              key={seg.label}
              title={`${seg.label}: ${seg.count}`}
              style={{ width: `${(seg.count / total) * 100}%`, background: seg.color, minWidth: seg.count > 0 ? 4 : 0 }}
            />
          )
        ))}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {segments.map(seg => (
          <div key={seg.label} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: seg.color }} />
            {seg.label}: {seg.count.toLocaleString()}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function NetworkInsightsPage() {
  const [data, setData] = useState<InsightsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MessageResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState<'warmth' | 'companies' | 'dormant' | 'messages'>('warmth');

  useEffect(() => {
    loadInsights();
  }, []);

  const loadInsights = async () => {
    try {
      const res = await api.get('/linkedin/insights');
      setData(res.data);
    } catch (err) {
      console.error('Failed to load insights:', err);
    } finally {
      setLoading(false);
    }
  };

  const searchMessages = async () => {
    if (searchQuery.length < 2) return;
    setSearching(true);
    try {
      const res = await api.get('/linkedin/messages/search', { params: { q: searchQuery, limit: 50 } });
      setSearchResults(res.data.results);
    } catch (err) {
      console.error('Message search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const totalContacts = useMemo(() => {
    if (!data) return 0;
    return data.by_source.reduce((sum, s) => sum + s.count, 0);
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 rounded-full" style={{ borderColor: 'var(--netrun-green)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center" style={{ color: 'var(--text-muted)' }}>
        <p>No network data available. Run a LinkedIn import first.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-display mb-1" style={{ color: 'var(--text-primary)' }}>
          Network Insights
        </h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Relationship intelligence from your LinkedIn network
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { icon: Users, label: 'Total Contacts', value: totalContacts.toLocaleString(), color: 'var(--netrun-green)' },
          { icon: Building2, label: 'Companies', value: data.top_companies.length.toString(), color: '#3b82f6' },
          { icon: Flame, label: 'Hot Contacts', value: ((data.warmth_distribution.hot || 0) + (data.warmth_distribution.very_hot || 0)).toString(), color: '#f59e0b' },
          { icon: AlertTriangle, label: 'Dormant', value: data.dormant_contacts.length.toString(), color: '#ef4444' },
        ].map(card => (
          <div
            key={card.label}
            className="rounded-lg p-4"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}
          >
            <div className="flex items-center gap-2 mb-2">
              <card.icon size={16} style={{ color: card.color }} />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{card.label}</span>
            </div>
            <div className="text-2xl font-bold" style={{ color: card.color }}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* Warmth Distribution */}
      <div
        className="rounded-lg p-4 mb-6"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}
      >
        <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>
          Relationship Warmth Distribution
        </h3>
        <WarmthHeatmap distribution={data.warmth_distribution} />
      </div>

      {/* Source Breakdown */}
      <div
        className="rounded-lg p-4 mb-6"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}
      >
        <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>Contact Sources</h3>
        <div className="space-y-2">
          {data.by_source.map(s => {
            const pct = totalContacts > 0 ? (s.count / totalContacts) * 100 : 0;
            return (
              <div key={s.source} className="flex items-center gap-3">
                <span className="text-xs w-40 truncate" style={{ color: 'var(--text-secondary)' }}>
                  {(s.source || 'unknown').replace(/_/g, ' ')}
                </span>
                <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--bg-primary)' }}>
                  <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: 'var(--netrun-green)' }} />
                </div>
                <span className="text-xs font-mono w-12 text-right" style={{ color: 'var(--text-muted)' }}>
                  {s.count.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 rounded-lg p-1" style={{ background: 'var(--bg-secondary)' }}>
        {[
          { key: 'warmth' as const, label: 'Top Contacts', icon: Flame },
          { key: 'companies' as const, label: 'Companies', icon: Building2 },
          { key: 'dormant' as const, label: 'Dormant', icon: AlertTriangle },
          { key: 'messages' as const, label: 'Messages', icon: MessageSquare },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm flex-1 justify-center transition-all"
            style={{
              background: activeTab === tab.key ? 'var(--netrun-green)' : 'transparent',
              color: activeTab === tab.key ? '#000' : 'var(--text-secondary)',
              cursor: 'pointer',
              border: 'none',
            }}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        className="rounded-lg"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}
      >
        {/* Top Warm Contacts */}
        {activeTab === 'warmth' && (
          <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
            {data.top_warm_contacts.map(contact => (
              <div key={contact.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {contact.name}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {[contact.title, contact.company].filter(Boolean).join(' at ')}
                  </div>
                </div>
                <div className="w-32">
                  <WarmthBar score={contact.warmth} />
                </div>
              </div>
            ))}
            {data.top_warm_contacts.length === 0 && (
              <div className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                No warmth data yet. Run import and warmth calculation first.
              </div>
            )}
          </div>
        )}

        {/* Companies */}
        {activeTab === 'companies' && (
          <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
            {data.top_companies.map(c => {
              const maxCount = data.top_companies[0]?.count || 1;
              return (
                <div key={c.company} className="flex items-center gap-3 px-4 py-3">
                  <span className="text-sm w-48 truncate" style={{ color: 'var(--text-primary)' }}>
                    {c.company}
                  </span>
                  <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--bg-primary)' }}>
                    <div
                      className="h-2 rounded-full"
                      style={{ width: `${(c.count / maxCount) * 100}%`, background: '#3b82f6' }}
                    />
                  </div>
                  <span className="text-xs font-mono w-8 text-right" style={{ color: 'var(--text-muted)' }}>
                    {c.count}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Dormant Contacts */}
        {activeTab === 'dormant' && (
          <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
            <div className="px-4 py-3" style={{ background: 'rgba(239, 68, 68, 0.05)' }}>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                These contacts have high warmth scores but no recent LinkedIn interaction (180+ days).
                Consider reaching out to re-engage.
              </p>
            </div>
            {data.dormant_contacts.map(contact => (
              <div key={contact.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {contact.name}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {[contact.title, contact.company].filter(Boolean).join(' at ')}
                  </div>
                  {contact.last_interaction && (
                    <div className="text-xs mt-0.5" style={{ color: '#ef4444' }}>
                      Last interaction: {contact.last_interaction}
                    </div>
                  )}
                </div>
                <div className="w-32">
                  <WarmthBar score={contact.warmth} />
                </div>
              </div>
            ))}
            {data.dormant_contacts.length === 0 && (
              <div className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                No dormant contacts found.
              </div>
            )}
          </div>
        )}

        {/* Message Search */}
        {activeTab === 'messages' && (
          <div>
            <div className="p-4 flex gap-2">
              <div className="flex-1 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchMessages()}
                  placeholder="Search LinkedIn messages..."
                  className="w-full pl-9 pr-3 py-2 rounded-lg text-sm"
                  style={{
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-primary)',
                    outline: 'none',
                  }}
                />
              </div>
              <button
                onClick={searchMessages}
                disabled={searching || searchQuery.length < 2}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{
                  background: 'var(--netrun-green)',
                  color: '#000',
                  cursor: searchQuery.length < 2 ? 'not-allowed' : 'pointer',
                  opacity: searchQuery.length < 2 ? 0.5 : 1,
                  border: 'none',
                }}
              >
                Search
              </button>
            </div>

            <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
              {searchResults.map(msg => (
                <div key={msg.id} className="px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-xs px-1.5 py-0.5 rounded"
                      style={{
                        background: msg.direction === 'sent' ? 'rgba(144, 185, 171, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                        color: msg.direction === 'sent' ? 'var(--netrun-green)' : '#3b82f6',
                      }}
                    >
                      {msg.direction === 'sent' ? 'Sent' : 'Received'}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {msg.direction === 'sent' ? `To: ${msg.to_name}` : `From: ${msg.from_name}`}
                    </span>
                    {msg.date && (
                      <span className="text-xs ml-auto" style={{ color: 'var(--text-muted)' }}>
                        {new Date(msg.date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {msg.content}
                  </p>
                  {msg.contact_name && (
                    <div className="text-xs mt-1" style={{ color: 'var(--netrun-green)' }}>
                      Linked to: {msg.contact_name}
                    </div>
                  )}
                </div>
              ))}
              {searchResults.length === 0 && searchQuery && !searching && (
                <div className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                  {searchQuery.length < 2 ? 'Type at least 2 characters to search' : 'No messages found'}
                </div>
              )}
              {!searchQuery && (
                <div className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                  Search across 16,768 LinkedIn messages
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
